import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'

export const useSiteMonitoring = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()
  const { scopes } = useSecurityContext()

  const useSiteMonitoringOverviewQuery = (filter: {}) => {
    return useQuery({
      queryKey: ['siteMonitoringOverview', filter],
      queryFn: ({ queryKey }) =>
        api.post('store_monitor/overview', {
          ...queryKey[1],
        }),
      staleTime: 60 * 1000,
    })
  }

  const useSiteTanksQuery = (id, filters) =>
    useQuery({
      queryKey: ['tankDetailsQuery', { id, filters }],
      queryFn: ({ queryKey }) =>
        api.post('store_monitor/details', {
          ids: [id],
          ...filters,
          carriers: filters.carriers || [],
          alerts: filters.alerts || [],
          stores: filters.stores || [],
          products: filters.products || [],
          tank_tags: filters.tank_tags || [],
        }),
      refetchOnWindowFocus: false,
    })

  const useSiteViewOrdersQuery = (id, start_date, end_date) =>
    useQuery({
      queryKey: ['siteViewOrders', { id, start_date, end_date }],
      queryFn: () =>
        addCarriersToOrders(() =>
          api.post(
            'store_monitor/site_view/orders',
            { id },
            { queryParams: new URLSearchParams({ start_date, end_date }) }
          )
        ),
      refetchOnWindowFocus: false,
    })

  const useSiteDetailsForecastQuery = ({ store_number, tank_id }) =>
    useQuery({
      queryKey: ['siteDetailsForecast', { store_number, tank_id }],
      queryFn: ({ queryKey }) =>
        api.post('store/forecast_grid', { store_number, tank_id, filters: { actual: 21, forecast: 10 } }),
      refetchOnWindowFocus: false,
      enabled: !!store_number && !!tank_id,
    })

  const useSiteDetailsTankReadingsQuery = ({ store_number, tank_id }) =>
    useQuery({
      queryKey: ['siteDetailsTankReadings', { store_number, tank_id }],
      queryFn: ({ queryKey }) =>
        api.post('store/tank_readings/overview', {}, { queryParams: new URLSearchParams({ store_number, tank_id }) }),
      refetchOnWindowFocus: false,
      enabled: !!store_number && !!tank_id && Object.hasOwn(scopes, 'site_monitoring'),
    })

  const useTankRundownQuery = (store_number, tank_id) =>
    useQuery({
      queryKey: ['tankRundownQuery', { store_number, tank_id }],
      queryFn: ({ queryKey }) => api.post('store_monitor/tank_rundown', queryKey[1]),
      refetchOnWindowFocus: false,
      enabled: !!store_number && !!tank_id,
    })

  const useForecastOverrideMutation = useMutation({
    mutationKey: ['forecastOverride'],
    mutationFn: (payload) => api.post('store_monitor/forecast/override/upsert', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['siteDetailsForecast'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['tankRundownQuery'],
      })
    },
  })

  const useDeleteForecastOverrideMutation = useMutation({
    mutationKey: ['deleteForecastOverride'],
    mutationFn: (payload) => api.post('store_monitor/forecast/override/delete', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['siteDetailsForecast'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['tankRundownQuery'],
      })
    },
  })

  const useSalesOverrideMutation = useMutation({
    mutationKey: ['salesOverride'],
    mutationFn: (payload) => api.post('store_monitor/sales/override/upsert', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['siteDetailsForecast'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['tankRundownQuery'],
      })
    },
    onError: (error) => {
      console.log('error', error)
    },
  })

  const useDeleteSalesOverrideMutation = useMutation({
    mutationKey: ['deleteSalesOverride'],
    mutationFn: (payload) => api.post('store_monitor/sales/override/delete', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['siteDetailsForecast'],
      })
      await queryClient.invalidateQueries({
        queryKey: ['tankRundownQuery'],
      })
    },
  })

  const useDeleteManualReadingMutation = useMutation({
    mutationKey: ['deleteManualReading'],
    mutationFn: (payload: { reading_id: string }) =>
      api.post('store_monitor/delete_manual_reading', undefined, { queryParams: new URLSearchParams(payload) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['siteDetailsTankReadings'] })
      await queryClient.invalidateQueries({ queryKey: ['tankRundownQuery'] })
    },
  })

  const useSiteByIdQuery = (id) =>
    useQuery({
      queryKey: ['SiteById', id],
      queryFn: () => api.post('store/by_id', { id }),
      refetchOnWindowFocus: false,
      enabled: !!id && id !== 'dashboard',
    })

  const useSitesByIdsQuery = (siteIds: string[]) =>
    useQueries({
      queries: siteIds.map((id) => ({
        queryKey: ['site', id],
        queryFn: () => api.post('store/by_id', { id }),
        enabled: !!id,
      })),
    })

  return {
    useSiteMonitoringOverviewQuery,
    useSiteTanksQuery,
    useSiteViewOrdersQuery,
    useSiteDetailsForecastQuery,
    useSiteDetailsTankReadingsQuery,
    useForecastOverrideMutation,
    useDeleteForecastOverrideMutation,
    useSalesOverrideMutation,
    useDeleteSalesOverrideMutation,
    useDeleteManualReadingMutation,
    useTankRundownQuery,
    useSiteByIdQuery,
    useSitesByIdsQuery,
  }
}

const addCarriersToOrders = async (getOrders) => {
  const data = await getOrders()
  return data.map((order) => ({ ...order, carrier: order.carrier || order.manager }))
}
